import React from "react";
import { Stack, Text, ActionButton, Slider } from "@fluentui/react";
import { VehicleCarFilled, LocationFilled, GasPumpFilled, SettingsFilled } from "@fluentui/react-icons";
import styles from "./ResultsPanel.module.css";
import { ScoreCards } from "../CarCards/ScoreCards";
import { capitalizeFirstLetter } from "../../utils/textUtils";

interface CarListing {
    id_unico: string;
    marca: string;
    modelo: string;
    fecha_fabricacion: number;
    version?: string;
    precio: number;
    kilometraje: number;
    transmision: string;
    tipo_combustible: string;
    departamento?: string;
    imagen_url?: string;
    tipo_vendedor?: string;
    precio_mercado_promedio?: number;
    precio_mercado_minimo?: number;
    precio_mercado_maximo?: number;
    transparencia?: number;
    rebaja?: number;
    views_count?: number;
}

interface Props {
    listings: CarListing[];
    isLoading: boolean;
    onCarSelect: (car: CarListing) => void;
    onSuggestionClick?: (suggestion: string) => void;
    isEmpty: boolean;
    searchSummary?: string;
    selectedCars?: string[];
    onToggleSelection?: (carId: string) => void;
    savedCarIds?: Set<string>;
}

const SuggestionChips = ({ onSuggestionClick }: { onSuggestionClick: (suggestion: string) => void }) => {
    const suggestions = [
        { text: "Toyota Hilux doble cabina en Managua" },
        { text: "Hyundai Tucson automático" },
        { text: "Toyota Prado Diesel 2018 o mas nuevo" },
        { text: "Kia Sportage en Managua y Masaya" },
        { text: "Camionetas 4x4 en Managua" },
        { text: "Auto económico unico dueño" }
    ];

    return (
        <div className={styles.suggestionGrid}>
            {suggestions.map((suggestion, index) => (
                <button
                    key={index}
                    className={styles.suggestionChip}
                    onClick={() => onSuggestionClick(suggestion.text)}
                >
                    <span className={styles.chipText}>{suggestion.text}</span>
                </button>
            ))}
        </div>
    );
};

const CarCard = ({
    car,
    onSelect,
    isSelected = false,
    onToggleSelection,
    isSaved = false
}: {
    car: CarListing;
    onSelect: (car: CarListing) => void;
    isSelected?: boolean;
    onToggleSelection?: (carId: string) => void;
    isSaved?: boolean;
}) => {
    const getPriceIndicator = () => {
        if (!car.precio_mercado_promedio) return "En mercado";
        const ratio = car.precio / car.precio_mercado_promedio;
        if (ratio < 0.9) return "Por debajo";
        if (ratio > 1.1) return "Por encima";
        return "En mercado";
    };

    const getPriceIndicatorColor = () => {
        const indicator = getPriceIndicator();
        switch (indicator) {
            case "Por debajo": return "#10b981"; // green
            case "Por encima": return "#ef4444"; // red
            default: return "#6b7280"; // gray
        }
    };

    return (
        <div className={`${styles.carCard} ${isSelected ? styles.selected : ""}`} onClick={() => onSelect(car)}>
            {onToggleSelection && (
                <div
                    className={styles.selectionCheckbox}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelection(car.id_unico);
                    }}
                >
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className={styles.checkbox}
                    />
                    <span className={styles.checkmark}></span>
                </div>
            )}
            <div className={styles.carImageContainer}>
                {car.imagen_url ? (
                    <img
                        src={car.imagen_url}
                        alt={`${car.marca} ${car.modelo}`}
                        className={styles.carImage}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div
                    className={styles.carImagePlaceholder}
                    style={{ display: car.imagen_url ? 'none' : 'flex' }}
                >
                    <VehicleCarFilled style={{ fontSize: 48, color: "#6b7280" }} />
                </div>
                {isSaved && (
                    <div
                        className={styles.savedIndicator}
                        title="Guardado"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#0078d4">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                        </svg>
                    </div>
                )}
            </div>
            
            <div className={styles.carDetails}>
                <div className={styles.carContent}>
                    <Text className={styles.carTitle}>
                        {car.fecha_fabricacion} {car.marca} {car.modelo}
                        {car.version && car.version.toUpperCase() !== 'SIN ESPECIFICAR' && ` • ${car.version}`}
                    </Text>
                    
                    <div className={styles.priceContainer}>
                        {car.rebaja && Number(car.rebaja) > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Text className={styles.price}>${(car.precio || 0).toLocaleString()}</Text>
                                <span 
                                    style={{ 
                                        backgroundColor: '#22c55e', 
                                        color: 'white', 
                                        padding: '1px 4px', 
                                        borderRadius: '3px', 
                                        fontSize: '10px', 
                                        fontWeight: 'bold' 
                                    }}
                                >
                                    -{Number(car.rebaja)}%
                                </span>
                            </div>
                        ) : (
                            <Text className={styles.price}>${(car.precio || 0).toLocaleString()}</Text>
                        )}
                    </div>

                    <Stack horizontal wrap tokens={{ childrenGap: 4 }} className={styles.chipContainer}>
                        <div className={styles.chip}>
                            <SettingsFilled style={{ fontSize: 12 }} />
                            <span>{capitalizeFirstLetter(car.transmision)}</span>
                        </div>
                        <div className={styles.chip}>
                            <GasPumpFilled style={{ fontSize: 12 }} />
                            <span>{capitalizeFirstLetter(car.tipo_combustible)}</span>
                        </div>
                        {car.tipo_vendedor && (
                            <div className={styles.chip}>
                                <span>{capitalizeFirstLetter(car.tipo_vendedor)}</span>
                            </div>
                        )}
                        {car.departamento && (
                            <div className={styles.chip}>
                                <LocationFilled style={{ fontSize: 12 }} />
                                <span>{capitalizeFirstLetter(car.departamento)}</span>
                            </div>
                        )}
                        {car.views_count !== undefined && car.views_count >= 5 && (
                            <div className={styles.viewsChip}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                                <span>{car.views_count} vistas</span>
                            </div>
                        )}
                    </Stack>

                    <Text className={styles.mileage}>
                        Kilometraje: {(car.kilometraje || 0).toLocaleString()} km
                    </Text>

                    {/* New Score Cards Section */}
                    <ScoreCards 
                        transparencia={car.transparencia}
                        precio={car.precio}
                        precioMercadoMinimo={car.precio_mercado_minimo}
                        precioMercadoPromedio={car.precio_mercado_promedio}
                        precioMercadoMaximo={car.precio_mercado_maximo}
                    />
                </div>

                <div className={styles.actions}>
                    <ActionButton
                        className={styles.actionButtonPrimary}
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent card click
                            onSelect(car); // This opens the car detail drawer
                        }}
                        styles={{
                            root: {
                                border: '1px solid var(--color-accent-primary)',
                                background: 'var(--color-accent-primary)',
                                color: 'var(--color-text-primary)',
                                minHeight: '36px',
                                maxHeight: '36px',
                                fontSize: 'var(--font-size-xs)',
                                padding: '0',
                                margin: '0'
                            }
                        }}
                    >
                        Ver detalle
                    </ActionButton>
                </div>
            </div>
        </div>
    );
};

export const ResultsPanel = ({
    listings,
    isLoading,
    onCarSelect,
    onSuggestionClick,
    isEmpty,
    searchSummary,
    selectedCars = [],
    onToggleSelection,
    savedCarIds = new Set()
}: Props) => {
    const [columnCount, setColumnCount] = React.useState(1);
    
    const handleSuggestionClick = (suggestion: string) => {
        if (onSuggestionClick) {
            onSuggestionClick(suggestion);
        }
    };
    
    const handleColumnChange = (value: number) => {
        setColumnCount(Math.round(value));
    };

    if (isEmpty) {
        return (
            <div className={styles.resultsPanel}>
                <div className={styles.emptyState}>
                    <div className={styles.emptyStateHeader}>
                        <Text className={styles.emptyTitle}>
                            Explora Sugerencias
                        </Text>
                        <Text className={styles.emptySubtitle}>
                            Comienza con una de estas búsquedas populares
                        </Text>
                    </div>
                    <SuggestionChips onSuggestionClick={handleSuggestionClick} />
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.resultsPanel}>
                <div className={styles.loadingState}>
                    <Text>Buscando vehículos...</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.resultsPanel}>
            <div className={styles.resultHeader}>
                <div className={styles.headerTop}>
                    <div className={styles.columnControl}>
                        <Text className={styles.columnLabel}>Columnas:</Text>
                        <Slider
                            className={styles.columnSlider}
                            min={1}
                            max={3}
                            step={1}
                            value={columnCount}
                            onChange={handleColumnChange}
                            showValue={false}
                        />
                        <Text className={styles.columnValue}>{columnCount}</Text>
                    </div>
                </div>
                {searchSummary && (
                    <Text className={styles.searchSummary}>
                        {searchSummary}
                    </Text>
                )}
            </div>
            
            <div
                className={styles.carGrid}
                style={{
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                    '--columns': columnCount
                } as React.CSSProperties & { '--columns': number }}
            >
                {listings.map((car) => (
                    <CarCard
                        key={car.id_unico}
                        car={car}
                        onSelect={onCarSelect}
                        isSelected={selectedCars.includes(car.id_unico)}
                        onToggleSelection={onToggleSelection}
                        isSaved={savedCarIds.has(car.id_unico)}
                    />
                ))}
            </div>
        </div>
    );
};